import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// Import Components
import Table from '../Table';

// Import icons
import Search from '../../../assets/images/svg/search.svg';

// format date
import { dateFormat, sorted } from '../../tools/manager';

const AllCtmCouponsInterventions = () => {
  const navigation = useNavigate();

  //  🌱🌱 init store(services)   🌱🌱
  const getListInterventionCtmOrders = useStoreActions(
    (actions) => actions.middleware.OrdersMiddleware.getListInterventionCtmOrders
  );

  // 🌱 Etats (données + pagination + recherche)
  const [interventionCtmCoupons, setInterventionCtmCoupons] = useState([]);        // toutes les pages cumulées
  const [listInterventionCtmCoupons, setListInterventionCtmCoupons] = useState([]); // filtré (search)
  const [textSearch, setTextSearch] = useState('');

  const [nextToken, setNextToken] = useState(null);     // pagination token
  const [isLoadingMore, setIsLoadingMore] = useState(false); // état bouton "Charger plus"

  // 🔍 Filtre de recherche
  useEffect(() => {
    if (textSearch === '') {
      setListInterventionCtmCoupons(interventionCtmCoupons);
    } else {
      const lower = textSearch.toLowerCase();
      const filtered = interventionCtmCoupons.filter((element) => {
        // compat : certains champs sont en "worksite", d'autres "worksites"
        const ws = element?.worksites || element?.worksite || {};
        const companyName = ws?.company?.name?.toLowerCase() || '';
        const worksiteName = ws?.name?.toLowerCase() || '';
        const worksiteType = ws?.type?.name?.toLowerCase() || '';
        const numero = element?.numero || '';
        const d = dateFormat(element?.dateSigned);

        return (
          companyName.includes(lower) ||
          worksiteName.includes(lower) ||
          worksiteType.includes(lower) ||
          numero.includes(textSearch) ||
          d.day.includes(textSearch) ||
          d.month.includes(textSearch) ||
          d.year.includes(textSearch)
        );
      });

      setListInterventionCtmCoupons(filtered);
    }
  }, [interventionCtmCoupons, textSearch]);

  // 🌱 Charge une page de CTM (initiale ou "Charger plus")
  const fetchInterventionCtmCoupons = async (token = null) => {
    try {
      if (token) {
        setIsLoadingMore(true);
      }

      // 🔹 Appel avec pagination (limit + nextToken)
      const res = await getListInterventionCtmOrders({
        limit: 25,
        nextToken: token,
      });

      // 🔹 Compat : si le thunk renvoie encore un simple tableau
      const rawItems = Array.isArray(res) ? res : res?.items || [];
      const newToken = Array.isArray(res) ? null : res?.nextToken || null;

      // 🔹 Tu triais avant par numero desc : on garde ce comportement localement
      const sortedPage = sorted(rawItems, 'numero', true);

      // 🔹 Merge avec les précédentes pages
      setInterventionCtmCoupons((prev) =>
        token ? [...prev, ...sortedPage] : sortedPage
      );

      setNextToken(newToken);
    } catch (error) {
      console.error(error);
      toast.error(
        "❗️ Une erreur est survenue lors du chargement de la liste des bons d'intervention CTM"
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  //  🌱🌱  First effect  🌱🌱
  useEffect(() => {
    fetchInterventionCtmCoupons(null);
  }, []);

  // 🔁 Handler "Charger plus"
  const handleLoadMore = () => {
    if (nextToken && !isLoadingMore) {
      fetchInterventionCtmCoupons(nextToken);
    }
  };

  return (
    <section className="md:pt-8 px-4 md:px-9">
      {/* div search bar */}
      <div className="relative flex mt-6 border border-gray-admin rounded-xl">
        <input
          type="text"
          placeholder="Recherche bons"
          className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
          onInput={(e) => setTextSearch(e.target.value)}
        />

        <img
          src={Search}
          alt="search icon"
          className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
        />
      </div>

      {/* Table liste intervention ctm coupon */}
      <div className="mt-9">
        <Table>
          <thead>
            <tr>
              <th>Réalisé le :</th>
              <th>Numéro </th>
              <th>Type de chantier</th>
              <th>Nom du client</th>
              <th className="flex justify-end">Nom du chantier</th>
            </tr>
          </thead>
          <tbody>
            {listInterventionCtmCoupons?.length ? (
              listInterventionCtmCoupons
                // tu triés par numero ici aussi, on garde le comportement
                ?.sort((a, b) => b.numero - a.numero)
                ?.map((interventionCoupon) => {
                  const dateCouponCreate = dateFormat(
                    interventionCoupon?.dateSigned,
                    '.'
                  ).full;

                  const ws = interventionCoupon?.worksites || interventionCoupon?.worksite || {};

                  return (
                    <tr
                      key={interventionCoupon.id}
                      className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all`}
                      onClick={() => {
                        navigation(
                          `/pdf-preview/Pre-intervention/${interventionCoupon.id}`
                        );
                      }}
                    >
                      <td>{dateCouponCreate}</td>
                      <td>{interventionCoupon?.numero}</td>
                      <td>{ws?.type?.name}</td>
                      <td>{ws?.company?.name}</td>
                      <td className="flex justify-end mt-5">{ws?.name}</td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td
                  className="text-center text-lg font-semibold pt-20"
                  colSpan={6}
                >
                  {textSearch
                    ? "Oups, ce chantier ou client n'existe pas 🤭"
                    : "Ajouter un bon d'intervention"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Bouton "Charger plus" si pagination disponible */}
      {nextToken && (
        <div className="flex justify-center mt-6 mb-8">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isLoadingMore ? 'Chargement...' : 'Charger plus'}
          </button>
        </div>
      )}
    </section>
  );
};

export default AllCtmCouponsInterventions;
